import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronRight, ExternalLink } from 'lucide-react'
import { api } from '@/runtime'
import type { ClusterSummary, InfraOverview, InfraSection } from '@/db/api'
import { useDocPage } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { fmtAgo } from '@/lib/time'
import { alertDot, statusDot } from '@/lib/colour'
import { appIsOff } from '@/lib/agentGraph'
import { STATUS_WORDS, estateCards, type EstateCard } from '@/lib/infra'
import { Skeleton } from '@/components/Skeleton'
import { AppTags, HealthDot, HostDetail, ProblemDetail, useOnline } from '@/components/ClusterBits'
import { Storage, loadStorage } from '@/components/Storage'
import { HostsGlance, loadHosts } from '@/components/Hosts'
import { AppTabs, INFRA_TABS } from '@/components/AppTabs'
import { Failed, PageTitle, SectionTitle, Tag, cx } from '@/components/ui'

/**
 * Infrastructure, the infra pack's Overview (/infra; the page 2026-10-23, the
 * pack 2026-10-31): every estate at a glance (Hetzner, Home, Oracle Cloud,
 * Object storage, each green, amber or red by lib/infra.ts), then what fires
 * now, the machines that need a look, the security posture, the cluster's
 * state, what is stored where and what the security programme did. Online
 * only, each read once here and handed down: GET /api/infra (internal/infra:
 * Alertmanager and Prometheus, each section failing on its own), GET
 * /api/hosts (components/Hosts.tsx), GET /api/cluster and GET /api/storage
 * (components/Storage.tsx, linking to the Storage page). The write-up is docs/security-programme.md as
 * synced into the app by docsync, so it never loads. Lists are capped (show
 * less); a cut list ends in "and more" and Alertmanager's own UI has the
 * rest. Alert pushes open here (push.InfraPath); the Graph keeps its Infra view.
 */

const loadInfra = () => api.infra()
const loadCluster = () => api.cluster()

const ALERTMANAGER_UI = 'https://alertmanager.mermaid-great.ts.net'
// the docs page docsync keeps (its `doc` label is the file's path under docs/)
const PROGRAMME_DOC = 'security-programme.md'
const CAP = 8

const rowClass = 'flex items-start gap-2.5 px-3 py-2.5 min-h-12'
const linkClass = 'text-[13px] text-accent-text inline-flex items-center gap-1 min-h-11'

/** A part of the page: its heading and what is under it. */
const Part = ({ title, children, testId }: { title: string; children: ReactNode; testId: string }) => (
  <section className="mb-7" data-testid={testId}>
    <h2 className="t-title2 mb-3">{title}</h2>
    {children}
  </section>
)

/** A plain sentence on the list surface: nothing firing, nothing failing. */
const Calm = ({ children, testId }: { children: ReactNode; testId?: string }) => (
  <p className="card px-3 py-3 text-sm text-muted" data-testid={testId}>
    {children}
  </p>
)

/** The last row of a cut list. */
const More = () => <li className="px-3 min-h-10 flex items-center text-[13px] text-faint">And more</li>

/** A section whose source is off or failed says so; null when it answered. */
const SourceState = ({
  section,
  what,
  env,
  onRetry,
}: {
  section: InfraSection
  what: string
  env: string
  onRetry: () => void
}) =>
  section.state === 'off' ? (
    <Calm>
      Not set up on this server: <span className="font-mono text-[12px]">{env}</span> is not set.
    </Calm>
  ) : section.state === 'failed' ? (
    <Failed what={what} reason={section.error} onRetry={onRetry} />
  ) : null

const Alerts = ({
  data,
  error,
  reload,
  now,
}: {
  data: InfraOverview | null
  error: string
  reload: () => void
  now: number
}) => {
  const ui = data?.alertmanager_url || ALERTMANAGER_UI
  const open = (
    <a href={ui} target="_blank" rel="noreferrer" className={linkClass} data-testid="alertmanager-link">
      Open Alertmanager for the rest
      <ExternalLink size={13} aria-hidden />
    </a>
  )
  if (!data)
    return error ? (
      <Failed what="the alerts" reason={error} onRetry={reload} />
    ) : (
      <Skeleton className="h-40" />
    )
  const a = data.alerts
  if (a.state !== 'ok')
    return <SourceState section={a} what="the alerts" env="ALFIEOS_ALERTMANAGER_URL" onRetry={reload} />
  if (!a.groups.length)
    return (
      <>
        <Calm testId="alerts-calm">Nothing is firing. Alertmanager is quiet.</Calm>
        <a href={ui} target="_blank" rel="noreferrer" className={linkClass} data-testid="alertmanager-link">
          Open Alertmanager
          <ExternalLink size={13} aria-hidden />
        </a>
      </>
    )
  return (
    <>
      <ul className="group-list" data-testid="alerts">
        {a.groups.map((g) => (
          <li key={`${g.alert}/${g.value ?? ''}`} className={rowClass} data-testid="alert-group">
            <span
              className={cx('h-2 w-2 rounded-full shrink-0 mt-[7px]', alertDot[g.severity] ?? alertDot.none)}
              aria-label={g.severity}
            />
            <div className="flex-1 min-w-0">
              <p className="text-[15px] leading-5 font-medium break-words">{g.alert}</p>
              {/* the value once: TrivyFindings' summary already names its image, Falco's is its rule */}
              {g.value && !g.summary?.includes(g.value) && (
                <p className="text-[13px] text-fg-2 break-words leading-snug mt-0.5">{g.value}</p>
              )}
              {g.summary && (
                <p className="text-[13px] text-muted leading-snug mt-1 line-clamp-2">{g.summary}</p>
              )}
              <p className="text-[11px] text-faint mt-1">
                {g.since === g.latest
                  ? `Started ${fmtAgo(g.since, now)}`
                  : `Started ${fmtAgo(g.since, now)}, newest ${fmtAgo(g.latest, now)}`}
              </p>
            </div>
          </li>
        ))}
        {a.more && <More />}
      </ul>
      {open}
    </>
  )
}

/** One security list: its own state, else its rows (or the calm line when there are none). */
const SecurityList = ({
  title,
  what,
  section,
  env,
  reload,
  empty,
  more,
  children,
  count,
  testId,
}: {
  title: string
  /** what failed to load, for Failed: "Kyverno's results" */
  what: string
  section: InfraSection
  env: string
  reload: () => void
  empty: string
  more: boolean
  children: ReactNode
  count: number
  testId: string
}) => (
  <div className="mb-4" data-testid={testId}>
    <SectionTitle>{title}</SectionTitle>
    {section.state !== 'ok' ? (
      <SourceState section={section} what={what} env={env} onRetry={reload} />
    ) : count === 0 ? (
      <Calm>{empty}</Calm>
    ) : (
      <ul className="group-list">
        {children}
        {more && <More />}
      </ul>
    )}
  </div>
)

const Security = ({
  data,
  error,
  reload,
  now,
}: {
  data: InfraOverview | null
  error: string
  reload: () => void
  now: number
}) => {
  if (!data)
    return error ? (
      <Failed what="the security checks" reason={error} onRetry={reload} />
    ) : (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    )
  const { images, policies, falco } = data
  const image = (im: { image: string; critical: number }) => (
    <li key={im.image} className="flex items-center gap-2 px-3 min-h-12 py-2">
      <span className="flex-1 min-w-0 font-mono text-[12px] leading-4 break-all">{im.image}</span>
      <Tag tone="warn">{im.critical} critical</Tag>
    </li>
  )
  return (
    <>
      <SecurityList
        title="Our images with critical CVEs"
        what="the CVEs in our images"
        section={images}
        env="ALFIEOS_PROMETHEUS_URL"
        reload={reload}
        empty="None of the images built here has a critical CVE."
        more={images.ours_more}
        count={images.ours.length}
        testId="images-ours"
      >
        {images.ours.map(image)}
      </SecurityList>
      {images.state === 'ok' && (
        <SecurityList
          title="Third-party images with critical CVEs"
          what="the CVEs in third-party images"
          section={images}
          env="ALFIEOS_PROMETHEUS_URL"
          reload={reload}
          empty="No third-party image running has a critical CVE."
          more={images.theirs_more}
          count={images.theirs.length}
          testId="images-theirs"
        >
          {images.theirs.map(image)}
        </SecurityList>
      )}
      <SecurityList
        title="Kyverno audit failures"
        what="Kyverno's results"
        section={policies}
        env="ALFIEOS_PROMETHEUS_URL"
        reload={reload}
        empty="Every resource passes Kyverno's policies."
        more={policies.more}
        count={policies.policies.length}
        testId="policies"
      >
        {policies.policies.map((p) => (
          <li key={p.policy} className="flex items-center gap-2 px-3 min-h-12 py-2">
            <span className="flex-1 min-w-0 text-[14px] leading-5 break-words">{p.policy}</span>
            {p.severity && <Tag>{p.severity}</Tag>}
            <span className="text-[12px] text-muted tabular shrink-0">
              {p.failing} {p.failing === 1 ? 'resource' : 'resources'}
            </span>
          </li>
        ))}
      </SecurityList>
      <SecurityList
        title="Falco in the last day"
        what="Falco's events"
        section={falco}
        env="ALFIEOS_ALERTMANAGER_URL"
        reload={reload}
        empty="Falco has seen nothing in the last day."
        more={falco.more}
        count={falco.rules.length}
        testId="falco"
      >
        {falco.rules.map((r) => (
          <li key={r.rule} className="flex items-start gap-2 px-3 min-h-12 py-2.5">
            <div className="flex-1 min-w-0">
              <p className="text-[14px] leading-5 break-words">{r.rule}</p>
              <p className="text-[11px] text-faint mt-0.5">Newest {fmtAgo(r.last, now)}</p>
            </div>
            {r.priority && <Tag>{r.priority}</Tag>}
            <span className="text-[12px] text-muted tabular shrink-0 pt-0.5">
              {r.events} {r.events === 1 ? 'event' : 'events'}
            </span>
          </li>
        ))}
      </SecurityList>
      <div data-testid="edge">
        <SectionTitle>The edge</SectionTitle>
        <ul className="group-list">
          <li className="px-3 py-2.5 min-h-11 text-[14px] leading-5">
            Every public route only through Cloudflare.
          </li>
          <li className="px-3 py-2.5 min-h-11 text-[14px] leading-5">
            SSH to the master from the home IP only.
          </li>
        </ul>
      </div>
    </>
  )
}

const Cluster = ({
  data,
  error,
  reload,
}: {
  data: ClusterSummary | null
  error: string
  reload: () => void
}) => {
  if (!data)
    return error ? (
      <Failed what="the cluster" reason={error} onRetry={reload} />
    ) : (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-32" />
        <Skeleton className="h-16" />
      </div>
    )
  // an empty list may come as null from a build before graph-null-problems
  const nodes = data.nodes ?? []
  const apps = (data.apps ?? []).filter(appIsOff)
  const problems = data.problems ?? []
  return (
    <>
      <div className="mb-4" data-testid="cluster-nodes">
        <SectionTitle>Nodes</SectionTitle>
        <ul className="group-list">
          {nodes.map((n) => (
            <li key={n.name} className={rowClass}>
              <span className="flex mt-[6px]">
                <HealthDot health={n.ready && !n.pressure ? 'ok' : 'warn'} />
              </span>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <p className="text-[15px] leading-5 font-medium break-words">{n.name}</p>
                <HostDetail node={n} />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="mb-4" data-testid="cluster-apps">
        <SectionTitle>Argo CD apps that are off</SectionTitle>
        {apps.length === 0 ? (
          <Calm>Every app is synced and healthy.</Calm>
        ) : (
          <ul className="group-list">
            {apps.slice(0, CAP).map((a) => (
              <li key={a.name} className="flex items-center gap-2 px-3 min-h-12 py-2">
                <span className="flex-1 min-w-0 text-[14px] leading-5 break-words">{a.name}</span>
                <AppTags app={a} />
              </li>
            ))}
            {apps.length > CAP && <More />}
          </ul>
        )}
      </div>
      <div data-testid="cluster-problems">
        <SectionTitle>Pods that need a look</SectionTitle>
        {problems.length === 0 ? (
          <Calm>No pod needs a look.</Calm>
        ) : (
          <ul className="group-list">
            {problems.slice(0, CAP).map((p) => (
              <li key={`${p.namespace}/${p.name}`} className="flex flex-col gap-1 px-3 py-2.5 min-h-12">
                <ProblemDetail pod={p} />
              </li>
            ))}
            {problems.length > CAP && <More />}
          </ul>
        )}
      </div>
    </>
  )
}

/** The page's first lines as sentences: no headings, bullets or code marks. */
const firstLines = (text: string, n: number): string[] =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('#'))
    .map((l) => l.replace(/^[-*]\s+/, '').replace(/`/g, ''))
    .slice(0, n)

const Done = () => {
  const page = useDocPage(PROGRAMME_DOC)
  // synced data: nothing drawn until the first read, never a placeholder
  if (page === undefined) return null
  if (page === null)
    return <Calm testId="programme-missing">The write-up reaches AlfieOS docs with the next release.</Calm>
  return (
    <div className="card px-3 py-3" data-testid="programme">
      <ul className="flex flex-col gap-2">
        {firstLines(page.spec.text, 4).map((l, i) => (
          <li key={i} className="text-[14px] leading-snug line-clamp-3">
            {l}
          </li>
        ))}
      </ul>
      <Link to="/pages/$id" params={{ id: page.id }} className={cx(linkClass, 'mt-1')}>
        Read the whole of it
      </Link>
    </div>
  )
}

/** One estate: its dot, name, how it is, and what made it so. */
const Estate = ({ card }: { card: EstateCard }) => (
  <li className="card px-3 py-2.5 min-h-[84px] flex flex-col gap-1" data-testid={`estate-${card.id}`}>
    <div className="flex items-center gap-2">
      <span className={cx('h-2.5 w-2.5 rounded-full shrink-0', statusDot[card.status])} aria-hidden />
      <p className="text-[15px] leading-5 font-medium truncate">{card.name}</p>
    </div>
    <p className="text-[13px] leading-5 text-fg-2" data-status={card.status}>
      {STATUS_WORDS[card.status]}
    </p>
    {card.reasons.map((r) => (
      <p key={r} className="text-[12px] text-muted leading-snug line-clamp-2 break-words">
        {r}
      </p>
    ))}
  </li>
)

/**
 * The band of estates. Nothing read yet is Skeletons; then each card is judged
 * on whatever has answered (an estate with nothing read says so, never green).
 */
const Estates = ({ cards, loading }: { cards: EstateCard[]; loading: boolean }) =>
  loading ? (
    <div className="grid grid-cols-2 gap-2 mb-7" aria-busy="true" aria-label="Loading">
      {cards.map((c) => (
        <Skeleton key={c.id} className="h-[84px]" />
      ))}
    </div>
  ) : (
    <ul className="grid grid-cols-2 gap-2 mb-7" data-testid="estates" aria-label="Estates">
      {cards.map((c) => (
        <Estate key={c.id} card={c} />
      ))}
    </ul>
  )

export const InfraView = () => {
  const now = useNow(30_000)
  const infra = useOnline(loadInfra, 30_000)
  const cluster = useOnline(loadCluster, 30_000)
  const hosts = useOnline(loadHosts, 30_000)
  const storage = useOnline(loadStorage, 60_000)
  const cards = estateCards({ infra: infra.data, hosts: hosts.data, storage: storage.data })
  // a read that failed counts as answered: the cards then say what they could not read
  const loading = [infra, hosts, storage].every((r) => !r.data && !r.error)
  return (
    <div className="max-w-2xl" data-testid="infra">
      <PageTitle>Infrastructure</PageTitle>
      <AppTabs tabs={INFRA_TABS} label="Infrastructure" />
      <Estates cards={cards} loading={loading} />
      <Part title="Alerts firing now" testId="infra-alerts">
        <Alerts {...infra} now={now} />
      </Part>
      <Part title="Hosts" testId="infra-hosts">
        <HostsGlance {...hosts} />
      </Part>
      <Part title="Security" testId="infra-security">
        <Security {...infra} now={now} />
      </Part>
      <Part title="Cluster" testId="infra-cluster">
        <Cluster {...cluster} />
      </Part>
      <Part title="Storage" testId="infra-storage">
        <Storage {...storage} now={now} />
        <Link
          to="/infra/storage"
          className="text-[13px] text-accent-text inline-flex items-center gap-0.5 min-h-11"
          data-testid="storage-link"
        >
          Every drive, share and volume
          <ChevronRight size={14} aria-hidden />
        </Link>
      </Part>
      <Part title="What's been done" testId="infra-done">
        <Done />
      </Part>
    </div>
  )
}
